import { useRef, useState, useEffect } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, TextField, Button, Stack } from "@mui/material"
import fetchwrapper from "../../services/interceptors/fetchwrapper"
import BackIcon from "../../components/BackIcon"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate"
import CustomFieldsetAccordion from "../../components/CustomFieldsetAccordion"
import CustomBackdrop from "../../components/CustomBackdrop"
import CustomAutocomplete from "../../components/CustomAutocomplete"
import CustomDatePicker from "../../components/CustomDatePicker"
import dayjs from "dayjs"
import CustomFieldset from "../../components/CustomFieldset"
import CustomAggregationSegregationComponent from "../../components/CustomAggregationSegregationComponent.js"
import { useQuery, api, useMutation } from "../../api"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const ContainerFiltrosGenerales = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridTemplateRows: "auto auto auto",
  gridTemplateAreas: `
          "IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario"
          "ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad"
          "IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha"
          "IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar"
        `,
  gap: "8px",
  rowGap: "12px",
  alignItems: "center",
}))

const IUsuario = styled(Box)({
  gridArea: "IUsuario",
})

const ILocalidad = styled(Box)({
  gridArea: "ILocalidad",
})

const IRangoFecha = styled(Box)({
  gridArea: "IRangoFecha",
})

const IBtnFiltrar = styled(Box)({
  gridArea: "IBtnFiltrar",
})

const ContainerRangoFechaInner = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridTemplateRows: "auto",
  gridTemplateAreas: `
    "IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal"
  `,
  gap: "8px",
  alignItems: "flex-start",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto auto",
    gridTemplateAreas: `
      "IFechaInicial"
      "IFechaFinal"
    `,
    gap: "12px",
  },
}))

const RangoFechaIFechaInicial = styled(Box)({
  gridArea: "IFechaInicial",
})

const RangoFechaIFechaFinal = styled(Box)({
  gridArea: "IFechaFinal",
})

const PlanificacionVSEjecucionLabores = () => {
  const [expanded, setExpanded] = useState(true)

  // Calcular fechas por defecto: hoy y hace 1 mes
  const hoy = dayjs()
  const haceUnMes = dayjs().subtract(1, "month")

  const [filtrosGenerales, setFiltrosGenerales] = useState({
    usuarioCB: null,
    emcodemp: "",
    emapellido: "",
    emnombre: "",
    localidadCB: null,
    loccodigo: "",
    locdescri: "",
    fechaDesde: haceUnMes,
    fechaHasta: hoy,
  })

  const handleToggle = () => {
    setExpanded(!expanded)
  }

  const handleSetFiltrosGenerales = (k, v) => setFiltrosGenerales((prev) => ({ ...prev, [k]: v }))

  // Obtener lista de usuarios/empleados
  const {
    data: { data: dataUsuarios } = [],
    isFetching: isFetchingUsuarios,
    isLoading: isLoadingUsuarios,
  } = useQuery({
    queryKey: ["PlanificacionUsuarios"],
    url: "/PlanificacionVSEjecucionLabores/getAllEmpleados",
    showSuccess: "silent",
  })

  // Obtener lista de localidades
  const {
    data: { data: dataLocalidades } = [],
    isFetching: isFetchingLocalidades,
    isLoading: isLoadingLocalidades,
  } = useQuery({
    queryKey: ["PlanificacionLocalidades"],
    url: "/PlanificacionVSEjecucionLabores/getAllLocalidades",
    showSuccess: "silent",
  })

  // Obtener datos de planificación vs ejecución
  const {
    mutate: fetchPlanificacion,
    data: { data: dataPlanificacion } = { data: [] },
    isPending: isPendingPlanificacion,
    error: errorPlanificacion,
  } = useMutation({
    fn: async (filtros) => {
      const result = await api.post("/PlanificacionVSEjecucionLabores/getInfoTable", {
        externalFilters: {
          emcodemp: filtros.emcodemp,
          loccodigo: filtros.loccodigo,
          fechaDesde: filtros.fechaDesde,
          fechaHasta: filtros.fechaHasta,
        },
      })

      return result || []
    },
    enabled: false,
    showSuccess: "silent",
  })

  const handleClickFiltrarBtn = () => {
    // Validar que las fechas existan (requeridas por el backend)
    if (!filtrosGenerales.fechaDesde && !filtrosGenerales.fechaHasta) {
      return alert("Debe especificar un rango de fechas")
    }
    fetchPlanificacion({
      emcodemp: filtrosGenerales.emcodemp || "",
      loccodigo: filtrosGenerales.loccodigo || "",
      fechaDesde: filtrosGenerales.fechaDesde?.format("YYYY-MM-DD"),
      fechaHasta: filtrosGenerales.fechaHasta?.format("YYYY-MM-DD"),
    })
  }

  // Efecto para cargar datos iniciales (fecha actual y mes anterior)
  useEffect(() => {
    fetchPlanificacion({
      emcodemp: filtrosGenerales.emcodemp || "",
      loccodigo: filtrosGenerales.loccodigo || "",
      fechaDesde: filtrosGenerales.fechaDesde?.format("YYYY-MM-DD"),
      fechaHasta: filtrosGenerales.fechaHasta?.format("YYYY-MM-DD"),
    })
  }, [])

  // Procesar error del backend
  useEffect(() => {
    if (errorPlanificacion) {
      console.error("Error en consulta:", errorPlanificacion)
    }
  }, [errorPlanificacion])

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Planificación vs Ejecución</b>
        </div>

        <CustomBackdrop
          isLoading={
            isLoadingUsuarios ||
            isFetchingUsuarios ||
            isLoadingLocalidades ||
            isFetchingLocalidades ||
            isPendingPlanificacion
          }
        />

        <Box className={StyledRoot}>
          <CustomFieldsetAccordion title="Filtros Generales" expanded={expanded} onToggle={handleToggle}>
            <ContainerFiltrosGenerales>
              <IUsuario>
                <CustomAutocomplete
                  label="Empleado"
                  selectedOption={filtrosGenerales.usuarioCB}
                  setSelectedOption={(v) => {
                    setFiltrosGenerales((prev) => ({
                      ...prev,
                      usuarioCB: v,
                      emcodemp: v?.emcodemp ?? "",
                    }))
                  }}
                  options={dataUsuarios}
                />
              </IUsuario>

              <ILocalidad>
                <CustomAutocomplete
                  label="Localidad"
                  selectedOption={filtrosGenerales.localidadCB}
                  setSelectedOption={(v) => {
                    setFiltrosGenerales((prev) => ({
                      ...prev,
                      localidadCB: v,
                      loccodigo: v?.loccodigo ?? "",
                      locdescri: v?.locdescri ?? "",
                    }))
                  }}
                  options={dataLocalidades}
                />
              </ILocalidad>

              <IRangoFecha>
                <CustomFieldset title="Rango de Fechas Planificadas" sx={{ marginTop: "15px" }}>
                  <br />
                  <ContainerRangoFechaInner>
                    <RangoFechaIFechaInicial>
                      <CustomDatePicker
                        label="Fecha Desde"
                        value={filtrosGenerales.fechaDesde}
                        setValue={(v) => handleSetFiltrosGenerales("fechaDesde", v)}
                        isOptional={true}
                        sx={{ width: "100%" }}
                      />
                    </RangoFechaIFechaInicial>
                    <RangoFechaIFechaFinal>
                      <CustomDatePicker
                        label="Fecha Hasta"
                        value={filtrosGenerales.fechaHasta}
                        setValue={(v) => handleSetFiltrosGenerales("fechaHasta", v)}
                        isOptional={true}
                        sx={{ width: "100%" }}
                      />
                    </RangoFechaIFechaFinal>
                  </ContainerRangoFechaInner>
                </CustomFieldset>
              </IRangoFecha>

              <IBtnFiltrar>
                <Button
                  variant="contained"
                  onClick={handleClickFiltrarBtn}
                  disabled={!filtrosGenerales.fechaDesde && !filtrosGenerales.fechaHasta}
                  fullWidth
                >
                  Filtrar
                </Button>
              </IBtnFiltrar>
            </ContainerFiltrosGenerales>
          </CustomFieldsetAccordion>

          {dataPlanificacion && dataPlanificacion.length > 0 && (
            <CustomAggregationSegregationComponent
              data={dataPlanificacion}
              columnsConfig={() => [
                {
                  accessorKey: "usrcodigoseg",
                  header: "Usuario",
                  size: 200,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "emapellido",
                  header: "Apellido",
                  size: 200,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "emnombre",
                  header: "Nombre",
                  size: 200,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "locdescri",
                  header: "Localidad",
                  size: 200,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "hordescri",
                  header: "Horario",
                  size: 250,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "planfecha",
                  header: "Fecha Planficada",
                  size: 220,
                  Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
                },
                {
                  accessorKey: "horini",
                  header: "Hora Inicio",
                  size: 200,
                  Cell: ({ cell }) => <span>{cell.getValue() ? dayjs(cell.getValue()).format("HH:mm") : ""}</span>,
                },
                {
                  accessorKey: "horfin",
                  header: "Hora Fin",
                  size: 200,
                  Cell: ({ cell }) => <span>{cell.getValue() ? dayjs(cell.getValue()).format("HH:mm") : ""}</span>,
                },
                {
                  accessorKey: "ejecnumero",
                  header: "ID Ejecución",
                  size: 200,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "ejechora",
                  header: "Hora Ejecución",
                  size: 200,
                  Cell: ({ cell }) => <span>{cell.getValue() ? dayjs(cell.getValue()).format("HH:mm:ss") : ""}</span>,
                },
                {
                  accessorKey: "ejechoraAnt",
                  header: "Hora Anterior",
                  size: 210,
                  Cell: ({ cell }) => <span>{cell.getValue() ? dayjs(cell.getValue()).format("HH:mm:ss") : ""}</span>,
                },
                {
                  accessorKey: "minutoslab",
                  header: "Minutos Laborados",
                  size: 250,
                  accessorFn: (row) => Number(row.minutoslab) || 0,
                  Cell: ({ cell }) => (
                    <span>
                      {cell.getValue()?.toLocaleString?.("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      min
                    </span>
                  ),
                },
              ]}
              tableOptions={{
                initialState: {
                  sorting: [{ id: "planfecha", desc: true }],
                },
              }}
            />
          )}

          {dataPlanificacion.length === 0 && !isPendingPlanificacion && (
            <Box sx={{ textAlign: "center", mt: 4, color: "text.secondary" }}>
              No se encontraron registros para los filtros seleccionados
            </Box>
          )}
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default PlanificacionVSEjecucionLabores
